import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface CourierService {
    id: string;
    name: string;
    estimation: string;
    price: number;
}

export interface CourierOption {
    id: string;
    name: string;
    /** Courier logo; without one the badge shows badgeText */
    icon?: string;
    badgeText: string;
    badgeBgColor: string;
    services: CourierService[];
}

interface ShippingSelectorProps {
    options: CourierOption[];
    selectedServiceId?: string;
    onSelectService?: (service: CourierService, courierName: string) => void;
    className?: string;
}

const formatPrice = (price: number) => {
    return new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR',
        maximumFractionDigits: 0,
    })
        .format(price)
        .replace('Rp', 'Rp ');
};

const ShippingSelector = ({
    options,
    selectedServiceId = '',
    onSelectService,
    className,
}: ShippingSelectorProps) => {
    const [openCouriers, setOpenCouriers] = useState<Record<string, boolean>>({});

    const toggleAccordion = (courierId: string) => {
        setOpenCouriers((prev) => ({
            ...prev,
            [courierId]: !prev[courierId],
        }));
    };

    return (
        <div className={cn('flex flex-col gap-3 w-full', className)}>
            {options.map((courier) => {
                const hasSelectedService = courier.services.some(
                    (s) => s.id === selectedServiceId
                );
                const isOpen = openCouriers[courier.id] ?? hasSelectedService;

                return (
                    <div
                        key={courier.id}
                        className="w-full rounded-[16px] border border-blue-cs-200 bg-white p-4 shadowCard transition-all"
                    >
                        <button
                            type="button"
                            onClick={() => toggleAccordion(courier.id)}
                            className="flex w-full items-center justify-between text-left focus:outline-none"
                        >
                            <div className="flex items-center gap-3">
                                {courier.icon ? (
                                    <img
                                        src={courier.icon}
                                        alt=""
                                        className="h-8 w-11 shrink-0 rounded-[8px] object-contain"
                                    />
                                ) : (
                                    <div
                                        className={cn(
                                            'flex h-8 w-11 items-center justify-center rounded-[8px] text-xs font-bold',
                                            courier.badgeBgColor
                                        )}
                                    >
                                        {courier.badgeText}
                                    </div>
                                )}
                                <span className="title3bold text-black">
                                    {courier.name}
                                </span>
                            </div>

                            {isOpen ? (
                                <ChevronUp className="h-5 w-5 text-black" />
                            ) : (
                                <ChevronDown className="h-5 w-5 text-black" />
                            )}
                        </button>

                        {/* dropdown */}
                        {isOpen && (
                            <div className="mt-3 flex flex-col gap-2.5">
                                {courier.services.map((service) => {
                                    const isSelected = selectedServiceId === service.id;

                                    return (
                                        <button
                                            type="button"
                                            key={service.id}
                                            onClick={() =>
                                                onSelectService?.(service, courier.name)
                                            }
                                            className={cn(
                                                'flex w-full cursor-pointer items-center justify-between rounded-[12px] border p-3 text-left transition-all',
                                                isSelected
                                                    ? 'border-text-blue-100 bg-blue-50/60'
                                                    : 'border-blue-cs-200 bg-white hover:border-gray-300'
                                            )}
                                        >
                                            <div className="flex items-center gap-3 min-w-0 flex-1">

                                                <div
                                                    className={cn(
                                                        'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-all',
                                                        isSelected
                                                            ? 'border-text-blue-100 bg-text-blue-100'
                                                            : 'border-gray-400 bg-white'
                                                    )}
                                                >
                                                    {isSelected && (
                                                        <div className="h-2 w-2 rounded-full bg-white" />
                                                    )}
                                                </div>


                                                <div className="flex flex-col min-w-0">
                                                    <span className="title3bold text-black truncate">
                                                        {service.name}
                                                    </span>
                                                    <span className="body2 text-text-grey">
                                                        {service.estimation}
                                                    </span>
                                                </div>
                                            </div>

                                            <span className="title3bold text-black shrink-0 ml-2">
                                                {formatPrice(service.price)}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default ShippingSelector;